import axios from "axios";
import * as Sentry from "@sentry/vue";
import {
    apiSessionPaused,
    getAPISessionRevision,
    getAPISessionSignal
} from "@/services/apiSession";

let refreshPromise = null;
let refreshPromiseRevision;
let lastRefreshFailed = 0;
let lastRefreshFailedRevision;
let logoutEmitted = false;
const REFRESH_COOLDOWN_MS = 2000;

// Backstop for requests parked on an in-flight refresh. The wait is bounded
// by design (15s lock wait + 30s refresh POST), so exceeding this deadline
// means something is wedged — reject the parked request (a visible error
// beats an infinite spinner) but do NOT tear down the session: the refresh
// may still complete and later requests will use its token.
const REFRESH_WAIT_DEADLINE_MS = 60000;

function normalizedRequestPath(url) {
    try {
        const path = new URL(url, "https://api.forkto.invalid").pathname;
        return path
            .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,36}(?=\/|$)/gi, "/:id")
            .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, "/:id")
            .replace(/\/\d+(?=\/|$)/g, "/:id");
    } catch {
        return "unknown";
    }
}

function isTenantRead(config) {
    return (config?.method || "get").toLowerCase() === "get" &&
        /\/v[12]\//.test(normalizedRequestPath(config?.url));
}

function isStaleSession(config) {
    return config?._forktoAPISessionRevision !== undefined &&
        config._forktoAPISessionRevision !== getAPISessionRevision();
}

function isStaleRead(config) {
    return isTenantRead(config) && isStaleSession(config);
}

function canceledSessionRead(config) {
    return new axios.CanceledError("Read canceled because the authentication session changed", config);
}

function boundedDiagnosticCode(value) {
    return typeof value === "string" && /^[a-z0-9_-]{1,80}$/i.test(value) ? value : undefined;
}

function awaitRefreshWithDeadline(promise) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            const timeoutError = new Error("Timed out waiting for token refresh");
            timeoutError.isRefreshWaitTimeout = true;
            reject(timeoutError);
        }, REFRESH_WAIT_DEADLINE_MS);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (err) => {
                clearTimeout(timer);
                reject(err);
            }
        );
    });
}

export function setupAxiosInterceptors(authService) {
    axios.interceptors.request.use((config) => {
        if (isTenantRead(config)) {
            if (apiSessionPaused.value || isStaleRead(config)) throw canceledSessionRead(config);
            config._forktoAPISessionRevision = getAPISessionRevision();
            // Query-owned signals are canceled by QueryClient at the same
            // boundary. Other reads also get transport cancellation here.
            config.signal ||= getAPISessionSignal();
        }
        // Preserve write outcomes, but remember their actor so a delayed 401
        // cannot replay the old user's write with a newer user's token.
        if (/\/v[12]\//.test(normalizedRequestPath(config?.url))) {
            config._forktoAPISessionRevision ??= getAPISessionRevision();
        }
        return config;
    });
    axios.interceptors.response.use(
        (response) => {
            if (isStaleRead(response.config)) return Promise.reject(canceledSessionRead(response.config));
            return response;
        },
        async (error) => {
            // Aborted requests (e.g. superseded searches) are not failures.
            if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
                return Promise.reject(error);
            }
            const originalRequest = error.config;
            if (isStaleRead(originalRequest)) return Promise.reject(canceledSessionRead(originalRequest));
            const skipSentry = originalRequest?.skipSentry === true;

            // Only handle 401 errors for non-auth endpoints
            if (
                error.response?.status === 401 &&
                originalRequest && !originalRequest._retry &&
                !originalRequest.url?.includes("/login") &&
                !originalRequest.url?.includes("/refresh-token") &&
                !originalRequest.url?.includes("/logout")
            ) {
                if (apiSessionPaused.value || isStaleSession(originalRequest)) {
                    return Promise.reject(error);
                }
                originalRequest._retry = true;
                const revision = getAPISessionRevision();

                // If a recent refresh just failed, don't attempt another one immediately
                if ((!refreshPromise || refreshPromiseRevision !== revision) &&
                    lastRefreshFailedRevision === revision &&
                    Date.now() - lastRefreshFailed < REFRESH_COOLDOWN_MS) {
                    return Promise.reject(error);
                }

                // If a refresh is already in flight, wait for it instead of starting another
                if (!refreshPromise || refreshPromiseRevision !== revision) {
                    logoutEmitted = false;
                    refreshPromiseRevision = revision;
                    const refresh = authService
                        .refreshAccessToken()
                        .catch((err) => {
                            if (revision === getAPISessionRevision()) {
                                lastRefreshFailed = Date.now();
                                lastRefreshFailedRevision = revision;
                            }
                            throw err;
                        })
                        .finally(() => {
                            if (refreshPromise === refresh) refreshPromise = null;
                        });
                    refreshPromise = refresh;
                }

                try {
                    const newToken = await awaitRefreshWithDeadline(refreshPromise);
                    if (revision !== getAPISessionRevision()) throw canceledSessionRead(originalRequest);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } catch (refreshError) {
                    // An auth rejection may have cleared this session itself;
                    // it still needs the logout event below. A newer active
                    // session must never receive that previous user's logout.
                    if (revision !== getAPISessionRevision() && !apiSessionPaused.value) {
                        return Promise.reject(canceledSessionRead(originalRequest));
                    }
                    if (axios.isCancel(refreshError) || refreshError?.code === "ERR_CANCELED") {
                        return Promise.reject(refreshError);
                    }
                    if (refreshError.isRefreshWaitTimeout) {
                        Sentry.captureMessage("Token refresh wait exceeded deadline", {
                            level: "warning",
                            contexts: {
                                request: { url: originalRequest.url }
                            }
                        });
                        return Promise.reject(error);
                    }
                    // Only emit logout once per failed refresh cycle
                    if (!authService.isLoggingIn && !logoutEmitted) {
                        logoutEmitted = true;
                        Sentry.addBreadcrumb({
                            category: "auth",
                            message: "Forced logout: refresh failed in 401 interceptor",
                            level: "warning",
                            data: {
                                url: originalRequest.url,
                                status: refreshError.response?.status,
                                error: refreshError.message
                            }
                        });
                        authService.emitter.emit("logout");
                    }
                    return Promise.reject(refreshError);
                }
            }

            // Capture server errors (5xx) in Sentry
            if (!skipSentry && error.response?.status >= 500) {
                Sentry.captureException(error, {
                    contexts: {
                        response: {
                            status: error.response.status,
                            url: error.config?.url,
                            method: error.config?.method
                        }
                    }
                });
            }

            if (
                error.response?.status >= 400 &&
                error.response?.status < 500 &&
                !skipSentry &&
                ![401, 404, 422].includes(error.response.status)
            ) {
                const status = error.response.status;
                const method = (originalRequest?.method || "unknown").toUpperCase();
                const route = normalizedRequestPath(originalRequest?.url);
                Sentry.captureMessage("Unexpected client API error", {
                    level: "warning",
                    tags: {
                        api_method: method,
                        api_route: route,
                        http_status: String(status)
                    },
                    contexts: {
                        response: {
                            status,
                            route,
                            method,
                            feature: boundedDiagnosticCode(error.response.data?.feature),
                            code: boundedDiagnosticCode(error.response.data?.code)
                        }
                    },
                    fingerprint: ["api-client-error", method, route, String(status)]
                });
            }

            // Capture network errors (no response at all)
            if (!skipSentry && !error.response) {
                const method = (error.config?.method || "unknown").toUpperCase();
                const route = normalizedRequestPath(error.config?.url);
                const code = error.code || "NETWORK_ERROR";
                Sentry.captureException(error, {
                    tags: {
                        type: "network_error",
                        api_method: method,
                        api_route: route,
                        axios_code: code
                    },
                    contexts: {
                        request: {
                            method,
                            route,
                            timeout: error.config?.timeout ?? axios.defaults?.timeout
                        }
                    },
                    fingerprint: ["api-network-error", method, route, code]
                });
            }

            return Promise.reject(error);
        }
    );
}
